import { useMemo } from 'react'

import {
  matchFilterKey, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TemplatePageModel } from '@models/types'

import type { EntityOptions, ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['templatePagePartials']

type TemplatePagePartialsFilters = {
  brand_id?: number,
  hidden?: boolean,
  provider_id?: number,
  string?: string,
}

type UseTemplatePagePartialsOptions = {
  filters?: TemplatePagePartialsFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useTemplatePagePartials(options: UseTemplatePagePartialsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    brand_id: filterBrandId,
    hidden: filterHidden,
    provider_id: filterProviderId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { templatePagePartials: templatePagePartialsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { templatePagePartials } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredTemplatePagePartials = useMemo(() => {
    // @ts-expect-error TS2769
    const filtered = Object.values(templatePagePartials).filter((templatePagePartial: TemplatePageModel) => {
      const {
        // @ts-expect-error TS2339
        brand_id, hidden, id, provider_id, title, short_name,
      } = templatePagePartial

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchShortName = matchFilterString(short_name, filterString)
      const stringMatch = matchId || matchTitle || matchShortName
      // @ts-expect-error TS2345
      const matchBrandId = matchFilterKey(brand_id, filterBrandId)
      // @ts-expect-error TS2345
      const matchProviderId = matchFilterKey(provider_id, filterProviderId)
      const matchHidden = filterHidden === hidden

      return (matchBrandId && matchHidden && matchProviderId && stringMatch
      )
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [templatePagePartialsUpdatedAt, JSON.stringify(filters)])

  const filteredTemplatePagePartialsCount = filteredTemplatePagePartials.length
  const hasFilteredTemplatePagePartials = !!filteredTemplatePagePartialsCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTemplatePagePartialsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const { loading: loadingTemplatePagePartials } = useReduxAction(
    'templatePagePartials',
    'loadTemplatePagePartials',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTemplatePagePartials,
    filteredTemplatePagePartialsCount,
    hasFilteredTemplatePagePartials,
    loading: loadingTemplatePagePartials,
  }
}

export default useTemplatePagePartials
